import React, { useState, useEffect, useRef } from 'react';
import {
  FiChevronDown,
  FiZap,
  FiAlertTriangle,
  FiBookOpen,
  FiTarget,
  FiDollarSign,
  FiActivity,
  FiClock,
  FiUsers,
  FiBarChart2,
  FiTrendingUp,
  FiShield,
  FiInfo,
  FiDownload,
  FiRadio,
  FiX,
  FiSend,
  FiPaperclip
} from 'react-icons/fi';
import { Line } from 'react-chartjs-2';
import { Chart, LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Legend, Filler } from 'chart.js';
import Loading from '../components/Loading.jsx';
import { apiService } from '../service/Api.js';
import { useAuth } from '../context/AuthContext.js';
Chart.register(LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Legend, Filler);


// Color mapping for signal type badges
const signalTypeColors = {
  Objection: { bg: '#f87171', color: '#fff' }, // red
  Confusion: { bg: '#fbbf24', color: '#fff' }, // amber
  Expansion: { bg: '#34d399', color: '#fff' }, // green
  ChurnRisk: { bg: '#f43f5e', color: '#fff' }, // rose
  DealHealth: { bg: '#60a5fa', color: '#fff' }, // blue
  Whitespace: { bg: '#a78bfa', color: '#fff' }, // purple
  Competitive: { bg: '#fb7185', color: '#fff' }, // pink
  Integration: { bg: '#4ade80', color: '#fff' }, // lime
  RevOps: { bg: '#f59e42', color: '#fff' }, // orange
  Default: { bg: '#d1d5db', color: '#374151' }, // gray
};

// Modal component for popup
const Modal = ({ open, onClose, children, contentStyle }) => {
  if (!open) return null;
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
      background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 12, maxWidth: '90vw', maxHeight: '90vh',
        overflow: 'auto', ...contentStyle
      }} onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
};

const DealInsights = () => {
  const [insightData, setInsightData] = useState(null);
  const [insightsLog, setInsightsLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tenantId, setTenantId] = useState('');
  const [objectId, setObjectId] = useState('');
  const [insightType, setInsightType] = useState('');
  const [expandSections, setExpandSections] = useState({});
  const [extractedIds, setExtractedIds] = useState(null);
  const [loadingActionIndex, setLoadingActionIndex] = useState(null);
  const [srNo, setSrNo] = useState(null);
  const [accountNo, setAccountNo] = useState(null);
  const [notification, setNotification] = useState(null);
  const [dealAmount, setDealAmount] = useState(null);
  const [lastActivityCount, setLastActivityCount] = useState(null);
  const [lastActivityTimestamp, setLastActivityTimestamp] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [draftedEmailHtml, setDraftedEmailHtml] = useState('');
  const [attachmentUrl, setAttachmentUrl] = useState('');
  const [nbaIndex, setNbaIndex] = useState(null);
  const [emailEditError, setEmailEditError] = useState(null);
  const [emailHasChanges, setEmailHasChanges] = useState(false);
  const [originalEmailHtml, setOriginalEmailHtml] = useState('');
  const [collateralDesignBy, setCollateralDesignBy] = useState('');
  const [collateralType, setCollateralType] = useState('');
  const [collateralSlug, setCollateralSlug] = useState('');
  const [collateralAsset, setCollateralAsset] = useState(null);
  const emailEditorRef = useRef(null);
  const originalEmailHtmlRef = useRef(originalEmailHtml);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [noteSuccess, setNoteSuccess] = useState(false);
  const [showProcessingToaster, setShowProcessingToaster] = useState(false);
  const [entityId, setEntityId] = useState(null);

  // Template selection flow
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [pendingActionIndex, setPendingActionIndex] = useState(null);
  const [templateSearch, setTemplateSearch] = useState('');
  // Template-only (no NBA context) document generation
  const [isTemplateOnlyMode, setIsTemplateOnlyMode] = useState(false);
  const [isGeneratingDoc, setIsGeneratingDoc] = useState(false);

  // GTM Playbook: selection and submit state
  const [gtmSelectedSteps, setGtmSelectedSteps] = useState({}); // { [pathIdx]: number[] }
  const [gtmAddingIndex, setGtmAddingIndex] = useState(null);
  const [gtmAddedByPath, setGtmAddedByPath] = useState({}); // { [pathIdx]: true }

  const [risksExpanded, setRisksExpanded] = useState(false);
  const [gtmExpanded, setGtmExpanded] = useState(false);
  const [coachingExpanded, setCoachingExpanded] = useState(false);
  const [nbaExpanded, setNbaExpanded] = useState(false);
  const [showScoreTrend, setShowScoreTrend] = useState(false);
  const hasFetchedDataRef = useRef(false);
 
  const [pageContent, setPageContent] = useState(null); 

const {user, isAppInstalled} = useAuth();

console.log("user", user);
console.log("isAppInstalled", isAppInstalled);  


  useEffect(() => {
    originalEmailHtmlRef.current = originalEmailHtml;
  }, [originalEmailHtml]);

  // Auto-resize email editor when modal opens
  useEffect(() => {
    if (showEmailModal && emailEditorRef.current && draftedEmailHtml) {
      const element = emailEditorRef.current;
      // Small delay to ensure content is rendered
      setTimeout(() => {
        element.style.height = 'auto';
        const scrollHeight = element.scrollHeight;
        // Set a minimum height but allow expansion
        const newHeight = Math.max(scrollHeight, 200);
        element.style.height = `${newHeight}px`;
        // Add some padding for comfort
        element.style.minHeight = '200px';
        console.log('[DealInsights] Email editor resized:', { scrollHeight, newHeight });
      }, 100);
    }
  }, [showEmailModal, draftedEmailHtml]);

  // Handle window resize for email editor
  useEffect(() => {
    const handleResize = () => {
      if (showEmailModal && emailEditorRef.current) {
        const element = emailEditorRef.current;
        element.style.height = 'auto';
        const scrollHeight = element.scrollHeight;
        const newHeight = Math.max(scrollHeight, 200);
        element.style.height = `${newHeight}px`;
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [showEmailModal]);

  // Add this after all other hooks
  useEffect(() => {
    if (showEmailModal && emailEditorRef.current) {
      // Set content without dangerouslySetInnerHTML to prevent cursor jumping
      emailEditorRef.current.innerHTML = draftedEmailHtml || '';
    }
    // eslint-disable-next-line
  }, [showEmailModal, draftedEmailHtml]);

  // Utility: Convert plain text email to HTML paragraphs/line breaks if needed
  const formatEmailToHtml = (raw) => {
    if (!raw || typeof raw !== 'string') return '';
    const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(raw);
    if (looksLikeHtml) return raw;
    
    const escapeHtml = (str) => str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    
    const escaped = escapeHtml(raw.trim());
    
    // Check if it starts with "Subject:" - format it nicely
    // if (escaped.startsWith('Subject:')) {
    //   const lines = escaped.split('\n');
    //   const subjectLine = lines[0];
    //   const messageLines = lines.slice(1).filter(line => line.trim() !== '');
      
    //   const subjectHtml = `<div style="margin-bottom: 16px; padding: 12px; background-color: #f8f9fa; border-left: 4px solid #007bff; border-radius: 4px;">
    //   <strong style="color: #007bff;">${subjectLine}</strong>
    //   </div>`;
      
    //   const messageHtml = messageLines.length > 0 
    //     ? messageLines
    //         .join('\n')
    //         .split(/\n{2,}/)
    //         .map(p => `<p style="margin:0 0 12px; line-height: 1.6;">${p.replace(/\n/g, '<br/>')}</p>`)
    //         .join('')
    //     : '';
      
    //   return subjectHtml + messageHtml;
    // }
    
    // Regular paragraph formatting for other content
    const paragraphs = escaped
      .split(/\n{2,}/)
      .map(p => `<p style="margin:0 0 12px; line-height: 1.6;">${p.replace(/\n/g, '<br/>')}</p>`);
    return paragraphs.join('');
  };

  // Toggle selection of a GTM step checkbox
  const toggleGtmStepSelected = (pathIdx, stepIdx) => {
    setGtmSelectedSteps(prev => {
      const prevList = Array.isArray(prev[pathIdx]) ? prev[pathIdx] : [];
      const set = new Set(prevList);
      if (set.has(stepIdx)) {
        set.delete(stepIdx);
      } else {
        set.add(stepIdx);
      }
      return { ...prev, [pathIdx]: Array.from(set) };
    });
  };

  // Submit selected GTM steps as bulk HubSpot tasks
  const handleAddGtmTasks = async (path, pathIdx) => {
    const selectedArr = gtmSelectedSteps[pathIdx] || [];
    if (!selectedArr.length) {
      setNotification({ type: 'error', message: 'Please select at least one step to add as a task.' });
      return;
    }

    const selectedSet = new Set(selectedArr);
    const tasks = path.path_steps
      .filter((_, i) => selectedSet.has(i))
      .map(step => ({ hs_task_subject: step, hs_task_body: step }));

    setNotification({ type: 'info', message: 'Aura is currently adding task' });
    setGtmAddingIndex(pathIdx);
    try {
      const res = await apiService.createBulkTask(tasks, objectId, tenantId);
      if (res.data && res.data.success) {
        setNotification({ type: 'success', message: 'Tasks added successfully' });
        setGtmAddedByPath(prev => ({ ...prev, [pathIdx]: true }));
      } else {
        setNotification({ type: 'error', message: res.data?.message || 'Failed to add tasks. Please try again.' });
      }
    } catch (e) {
      setNotification({ type: 'error', message: 'Failed to add tasks. Please try again.' });
    } finally {
      setGtmAddingIndex(null);
    }
  };

  // Show processing toaster when new activity is detected
  useEffect(() => {
    if (lastActivityCount !== null && lastActivityCount > 0) {
      setShowProcessingToaster(true);
      // Auto-hide after 10 seconds
      const timer = setTimeout(() => {
        setShowProcessingToaster(false);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [lastActivityCount]);

  // Extract IDs and type from URL
  useEffect(() => {
    const extractFromUrl = (url) => {
      const tenantMatch = url.match(/contacts\/(\d+)/);
      const dealMatch = url.match(/0-3\/(\d+)/);

      if (tenantMatch) {
        setTenantId(tenantMatch[1]);
        setAccountNo(tenantMatch[1]);
      }

      if (dealMatch) {
        setObjectId(dealMatch[1]);
        setInsightType('deal');
      }
    };

    // Reset fetch flag when URL changes
    hasFetchedDataRef.current = false;
    
    const url = new URLSearchParams(window.location.search);
    const urlParam = url.get('url');
    if (urlParam) {
      extractFromUrl(urlParam);
    }
  }, []);

  // Fetch deal amount data and last activity info
  const fetchDealAmount = async (dealId) => {
    try {
      const result = await apiService.getHsDealData(dealId, accountNo);
      if (result.data.success && result.data.deal_amount) {
        setDealAmount(result.data.deal_amount);
      }
    } catch (error) {
      console.error('Error fetching deal amount:', error);
    }
  };

  // Fetch last activity info
  const fetchLastActivity = async (dealId) => {
    try {
      const result = await apiService.getHsLastActivity(dealId, accountNo);
      if (result.data.success) {
        setLastActivityCount(result.data.count);
        setLastActivityTimestamp(result.data.actual_last_engagement_timestamp);
      }
    } catch (error) {
      console.error('Error fetching last activity:', error);
    }
  };

  // Calculate score difference from insights_log - difference between last and first scores
  const calculateScoreDifference = (currentScore, insightsLog) => {
    if (!insightsLog || insightsLog.length === 0) {
      return null;
    }
    
    // Get the current score value
    const currentScoreValue = parseInt(currentScore.split('/')[0]);
    
    // Find the oldest score in the log
    let oldestScore = null;
    for (let i = insightsLog.length - 1; i >= 0; i--) {
      const log = insightsLog[i];
      const scoreStr = log?.insight?.companyinsight?.account_score || 
                      log?.companyinsight?.account_score ||
                      log?.account_score;
      
      if (scoreStr) {
        const scoreValue = parseInt(scoreStr.split('/')[0]);
        if (!isNaN(scoreValue)) {
          oldestScore = scoreValue;
        }
      }
    }
    
    if (oldestScore === null) {
      return null;
    }
    
    const difference = currentScoreValue - oldestScore;
    return { difference, oldestScore, currentScore: currentScoreValue };
  };

  // Helper to format time difference
  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const ts = new Date(timestamp);
    const diffMs = now - ts;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffHrs < 1) {
      if (diffMins < 1) {
        return '<1 min ago';
      } else {
        return `${diffMins} min ago`;
      }
    } else if (diffHrs < 24) {
      return `${diffHrs} hr ago`;
    } else {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    }
  };

  // Save collateral function
  const saveCollateral = async (entityId, emailHtml, pdfUrl, nbaIndex) => {
    try {
      console.log('💾 saveCollateral called with:', { entityId, emailHtml: emailHtml?.substring(0, 100) + '...', pdfUrl, tenantId, nbaIndex });
      const result = await apiService.saveCollateral(entityId, emailHtml, pdfUrl, tenantId, nbaIndex);
      if (!result.success) {
        console.error('Failed to save collateral:', result.error);
      }
      return result;
    } catch (error) {
      console.error('Error saving collateral:', error);
      throw error;
    }
  };

  // Add note to deal function
  const handleAddNote = async () => {
    if (!noteText.trim()) {
      setNotification({
        type: 'error',
        message: 'Please enter a note before submitting'
      });
      return;
    }

    if (!objectId) {
      setNotification({
        type: 'error',
        message: 'Please ensure you are on a deal page'
      });
      return;
    }

    setIsAddingNote(true);
    try {
      const result = await apiService.addNote(noteText.trim(), objectId, new Date().toISOString(), accountNo);
      
      
      if (result.data.success) {
        setNoteSuccess(true);
        setNotification({
          type: 'success',
          message: 'Note added successfully to the deal!'
        });
        // Clear the note text after success
        setTimeout(() => {
          setNoteText('');
          setShowNoteDialog(false);
          setNoteSuccess(false);
        }, 2000);
      } else {
        setNotification({
          type: 'error',
          message: `Failed to add note: ${result.data.error || 'Unknown error'}`
        });
      }
    } catch (error) {
      console.error('Error adding note:', error);
      setNotification({
        type: 'error',
        message: `Error adding note: ${error.message}`
      });
    } finally {
      setIsAddingNote(false);
    }
  };

  // Core execution — called after template choice is resolved
  const executeNbaWithTemplate = async (actionIndex, templateId, fieldsToBeUpdated) => {
    console.log('🚀 executeNbaWithTemplate:', { actionIndex, templateId, fieldsToBeUpdated });
    setLoadingActionIndex(actionIndex);
    setEmailEditError(null);
    try {
      const result = isAppInstalled
        ? await apiService.executeNba(objectId, actionIndex, accountNo, entityId, templateId, fieldsToBeUpdated)
        : await apiService.executeNbaWithoutApp(objectId, actionIndex, entityId, pageContent);
      console.log('📧 NBA execution result:', result.data);
      
      if (result.data.success) {
        // Handle the new response structure: {success: true, data: collateralObject} or {success: true, data: [collateralObject]}
        let collateralData = null;
        
        if (result.data.data) {
          if (Array.isArray(result.data.data) && result.data.data.length > 0) {
            // Array format
            collateralData = result.data.data[0];
          } else if (typeof result.data.data === 'object' && result.data.data.id) {
            // Single object format
            collateralData = result.data.data;
          }
        }
        
        // Debug logging to see the actual response structure
        console.log('📧 Response structure debug:', {
          hasData: !!result.data.data,
          dataType: typeof result.data.data,
          isArray: Array.isArray(result.data.data),
          hasId: result.data.data?.id,
          collateralData: collateralData
        });

        console.log("this is what is logging now",collateralData);
        console.log("this is what is logging now",collateralData.collateral);
        if (collateralData) {
          console.log('📧 Collateral data:', collateralData);
          
          // Store collateral asset if available
          if (collateralData.collateral?.collateral) {
            setCollateralAsset(collateralData.collateral.collateral);
            setCollateralDesignBy(collateralData.design_by);
          }
          
          // Check for email content in the new structure
          let emailContent = null;
          let pdfUrl = '';
          
          if (collateralData.collateral?.email) {
            // New structure: collateral.email contains the email data
            const emailData = collateralData.collateral.email;
            if (typeof emailData === 'string') {
              emailContent = emailData;
            } else if (emailData.subject && emailData.message_text) {
              // Format email with subject and message
              emailContent = `Subject: ${emailData.subject}\n\n${emailData.message_text}`;
            } else if (emailData.message_text) {
              emailContent = emailData.message_text;
            }
          } else if (collateralData.collateral?.email_message) {
            // Legacy structure fallback
            emailContent = collateralData.collateral.email_message;
          }
          
          // Debug logging to help troubleshoot
          console.log('📧 Email extraction debug:', {
            hasCollateral: !!collateralData.collateral,
            hasEmail: !!collateralData.collateral?.email,
            emailData: collateralData.collateral?.email,
            extractedContent: emailContent ? emailContent.substring(0, 100) + '...' : null,
            collateralType: collateralData.collateral_type,
            collateralSlug: collateralData.collateral_slug
          });
          
          // Check for PDF attachment
          if (collateralData.collateral?.pdf) {
            pdfUrl = collateralData.collateral.pdf;
          }
          
          if (emailContent) {
            const formattedMessage = formatEmailToHtml(emailContent);
            setDraftedEmailHtml(formattedMessage);
            setOriginalEmailHtml(formattedMessage);
            setAttachmentUrl(pdfUrl);
            setCollateralType(collateralData.collateral_type || '');
            setCollateralSlug(collateralData.collateral_slug || '');
            setShowEmailModal(true);
            setNbaIndex(actionIndex);
            setEmailHasChanges(false);
            
            setNotification({
              type: 'success',
              message: 'Email drafted successfully! Review and edit as needed.' 
            });
          } else if (collateralData.collateral_type === 'collateral' && collateralData.collateral_slug) {
            // Handle collateral type - open new tab
            const baseUrl = window.location.origin;
            const redirectTarget = `${baseUrl}/tailspin/collateral/${collateralData.collateral_slug}?source=aura`;
            const collateralUrl = `${baseUrl}/callback?token=${localStorage.getItem('aura_token')}&tenant_id=${localStorage.getItem('aura_tenant_id')}&redirect_url=${encodeURIComponent(redirectTarget)}`;
            window.open(collateralUrl, '_blank');
            
            setNotification({
              type: 'success',
              message: 'Collateral generated successfully! Opening in new tab.' 
            });
          } else {
            console.warn('📧 No email content found in response:', collateralData);
            setNotification({
              type: 'error',
              message: 'Email was generated but no content was found. Please try again.'
            });
          }
        } else {
          console.warn('📧 No collateral data found in response:', result.data);
          setNotification({
            type: 'error',
            message: 'No email data was returned. Please try again.'
          });
        }
      } else {
        setNotification({
          type: 'error',
          message: `Action failed: ${result.data.error || 'Unknown error'}`
        });
      }
    } catch (error) {
      console.error("API call error:", error);
      setNotification({
        type: 'error',
        message: `API call error: ${error.message}`
      });
    } finally {
      setLoadingActionIndex(null);
    }
  };

  // Entry point — fetch available templates, then show picker or execute directly
  const handleExecute = async (actionIndex) => {
    console.log('🚀 handleExecute called with actionIndex:', actionIndex);

    // For combined action (index 3), use template_info from first action if available
    const nbaActionList = (insightData?.insight?.nba?.nba_action || []).slice().sort((a, b) => b.signal_score - a.signal_score);
    const nba = actionIndex === 3 ? nbaActionList[0] : nbaActionList[actionIndex];
    const templateInfo = nba?.resource_requirements?.template_info;

    if (templateInfo?.id) {
      // Use template_info directly from insight — no picker needed
      await executeNbaWithTemplate(actionIndex, templateInfo.id, templateInfo.fields_to_be_updated);
      return;
    }

    // Fallback: no template_info — show template picker
    setIsLoadingTemplates(true);
    setPendingActionIndex(actionIndex);
    try {
      const res = await apiService.getNbaTemplates();
      const templates = res?.data?.data || [];
      if (templates.length > 0) {
        setAvailableTemplates(templates);
        setShowTemplateModal(true);
      } else {
        await executeNbaWithTemplate(actionIndex, null);
      }
    } catch (err) {
      console.error('Failed to fetch templates, proceeding without:', err);
      await executeNbaWithTemplate(actionIndex, null);
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  // Template-only: generate a document from a template without NBA context
  const handleTemplateOnlyGenerate = async (templateId) => {
    setIsGeneratingDoc(true);
    try {
      const result = await apiService.generateFromTemplate(objectId, templateId);
      if (result.data?.success && result.data?.redirectUrl) {
        window.open(result.data.redirectUrl, '_blank');
        setNotification({ type: 'success', message: 'Document generated! Opening in a new tab.' });
      }
    } catch (err) {
      console.error('generateFromTemplate error:', err);
      setNotification({ type: 'error', message: 'Failed to generate document. Please try again.' });
    } finally {
      setIsGeneratingDoc(false);
    }
  };

  // Open template modal in template-only mode (no NBA context)
  const handleGenerateDocument = async () => {
    setIsLoadingTemplates(true);
    try {
      const res = await apiService.getNbaTemplates();
      const templates = res?.data?.data || [];
      if (templates.length > 0) {
        setAvailableTemplates(templates);
        setIsTemplateOnlyMode(true);
        setShowTemplateModal(true);
      } else {
        setNotification({ type: 'error', message: 'No document templates are available.' });
      }
    } catch (err) {
      console.error('Failed to fetch templates:', err);
      setNotification({ type: 'error', message: 'Failed to load templates.' });
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  // Listen for page content data from Chrome extension
  useEffect(() => {
    const handleMessage = (event) => {
      // Log all messages to debug origin issues
      console.log('📄 [DealInsights] Received message:', {
        origin: event.origin,
        type: event.data?.type,
        data: event.data
      });

      // Accept messages from Chrome extension (content script runs in webpage context)
      // The origin will be the webpage's origin, not the frontend origin
      if (event.data.type === 'PAGE_CONTENT_DATA') {
        console.log('📄 [DealInsights] Received page content data:', {
          url: event.data.data.url,
          bodyTextLength: event.data.data.bodyText?.length || 0,
          timestamp: event.data.data.timestamp,
          bodyTextPreview: event.data.data.bodyText?.substring(0, 200) + '...'
        });
        
        // Log the full body text (be careful with very large content)
        console.log('📄 [DealInsights] Full page content:', event.data.data.bodyText);
        
        // You can also store it in state if needed
        setPageContent(event.data.data.bodyText);
        console.log('📄 [DealInsights] Page content stored in state, API call will be triggered');
        
      }
    };

    window.addEventListener('message', handleMessage);
    
    // Only request page content data when app is NOT installed
    if (!isAppInstalled) {
      console.log('📄 [DealInsights] App not installed, requesting page content data from extension...');
      window.parent.postMessage({ type: 'REQUEST_PAGE_CONTENT' }, '*');
    } else {
      console.log('📄 [DealInsights] App is installed, skipping page content request');
    }
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [isAppInstalled]);



  // Fetch insights when IDs and type are set AND (page content is available OR app is installed)
  useEffect(() => {
    const canFetchData = tenantId && objectId && accountNo && !hasFetchedDataRef.current;
    const hasRequiredData = isAppInstalled || pageContent;
    
    if (canFetchData && hasRequiredData) {
      console.log('[DealInsights] Starting API calls for:', { 
        tenantId, 
        objectId, 
        accountNo, 
        isAppInstalled,
        pageContentLength: pageContent?.length 
      });
      hasFetchedDataRef.current = true;
      setLoading(true);
      setError(null);
      setInsightData(null);
      
      // Conditionally call the appropriate API based on isAppInstalled
      const insightsPromise = isAppInstalled 
        ? apiService.getDealInsights(objectId, tenantId)
        : apiService.getdealinsightswithouttoken(objectId, pageContent);
      
      // Use Promise.allSettled to handle individual promise failures
      Promise.allSettled([
        fetchDealAmount(objectId),
        fetchLastActivity(objectId),
        insightsPromise
      ])
        .then(([dealAmountResult, lastActivityResult, insightsResult]) => {
          console.log('Deal amount result:', dealAmountResult);
          console.log('Last activity result:', lastActivityResult);
          console.log('Insights result:', insightsResult);
          
          // Handle insights result (the main one we care about)
          if (insightsResult.status === 'fulfilled') {
            const payload = insightsResult.value?.data;
            console.log('Payload:', payload);
            
            // Handle API error responses (success: false)
            if (payload && payload.success === false) {
              const errorMessage = payload.message || 'Failed to fetch deal insights';
              console.log("errorMessage", errorMessage);
              setError(errorMessage);
              return;
            }
            
            // Handle case running response
            if (payload && payload.success === true && payload.case === 'running') {
              const runningMessage = payload.message || 'Aura is currently processing the deal with new engagements. You will be notified via email as soon as updates become available.';
              console.log("Case running:", runningMessage);
              setError(runningMessage);
              return;
            }
            
            if (payload?.success && payload?.data) {
              // Handle legacy array response: [currentInsight, ...history]
              if (Array.isArray(payload.data)) {
                const arr = payload.data;
                if (arr.length > 0) {
                  setInsightData(arr[0]);
                  setInsightsLog(arr.slice(1));
                  setExtractedIds(arr[0].extractedIds);
                  setSrNo(arr[0].id);
                  setEntityId(arr[0].entity_id);
                } else {
                  setError('No deal data found');
                }
                return;
              }

              // Handle new object response
              if (typeof payload.data === 'object' && Object.keys(payload.data).length > 0) {
                const d = payload.data;
                const companyInsight = d.companyinsight || d.company_level_insight_response || d.insight?.companyinsight || {};
                const normalized = {
                  insight: {
                    companyinsight: companyInsight,
                    nba: d.nba || {},
                    signals: d.signals || {}
                  },
                  extractedIds: payload.extractedIds || d.extractedIds || null,
                  id: d.id || null,
                  entity_id: d.entity_id || null
                };

                setInsightData(normalized);
                setInsightsLog([]); // No history provided in new shape
                setExtractedIds(normalized.extractedIds);
                setSrNo(normalized.id);
                setEntityId(normalized.entity_id);
                return;
              }
            }

            setError('No deal data found');
          } else {
            // Insights promise was rejected
            console.error('Insights API Error:', insightsResult.reason);
            const error = insightsResult.reason;
            if (error.response?.data?.success === false && error.response?.data?.message) {
              setError(error.response.data.message);
            } else {
              setError('Failed to fetch deal insights');
            }
            hasFetchedDataRef.current = false; // Reset on error so user can retry
          }
        })
        .catch((e) => {
          console.error('Unexpected API Error:', e);
          setError('Failed to fetch deal insights');
          hasFetchedDataRef.current = false; // Reset on error so user can retry
        })
        .finally(() => setLoading(false));
    }
  }, [tenantId, objectId, accountNo, pageContent, isAppInstalled]);



  // Section toggling
  const toggleSection = (key) => {
    setExpandSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const Section = ({ title, iconComponent, children, collapsible, expanded, onToggle }) => (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-xl mb-6 p-6 transition-all duration-200">
      <div 
        className={`font-semibold text-sm mb-2 flex items-center gap-2 ${
          collapsible ? 'cursor-pointer' : ''
        }`}
        onClick={collapsible ? onToggle : undefined}
      >
        {iconComponent && React.createElement(iconComponent, { className: "w-4 h-4 text-gray-700" })} 
        {title}
        {collapsible && (
          <FiChevronDown
            className={`ml-auto w-5 h-5 text-gray-500 transition-transform duration-200 ${
              expanded ? 'rotate-0' : '-rotate-90'
            }`}
          />
        )}
      </div>
      {(!collapsible || expanded) && <div className="mt-4">{children}</div>}
    </div>
  );

  const renderDealInsights = () => {
    if (!insightData || !insightData.insight || !insightData.insight.companyinsight) {
      return null;
    }

    const companyInsight = insightData.insight.companyinsight;
    const nbaActions = (insightData.insight.nba?.nba_action || []).slice().sort((a, b) => b.signal_score - a.signal_score);
    const allSignals = (insightData.insight.signals?.signals || []);

    // Only show badges for signals mapped to NBA
    const nbaSignalIds = new Set(nbaActions.map(nba => nba.signal_reference_id));
    const mappedSignals = allSignals.filter(signal => nbaSignalIds.has(signal.signal_id));

    // --- Account Score History for Line Chart ---
    const getAccountScoreHistory = () => {
      if (!insightData || !companyInsight?.account_score) return [];
      
      // If no insightsLog, just return current score
      if (!insightsLog || insightsLog.length === 0) {
        const currentScore = parseInt(companyInsight.account_score.split('/')[0]);
        return [{ score: currentScore, timestamp: new Date().toISOString() }];
      }
      
      // Create score history from insights log
      const scoreHistory = insightsLog
        .map(log => {
          // Handle different possible data structures
          const scoreStr = log?.insight?.companyinsight?.account_score || 
                          log?.companyinsight?.account_score ||
                          log?.account_score;
          
          if (!scoreStr) return null;
          
          const num = parseInt(scoreStr.split('/')[0]);
          if (isNaN(num)) return null;
          
          // Handle different timestamp fields
          const timestamp = log.created_at || log.timestamp || log.insight?.created_at;
          
          return {
            score: num,
            timestamp: timestamp
          };
        })
        .filter(v => v !== null);
      
      // Add current score
      const currentScore = parseInt(companyInsight.account_score.split('/')[0]);
      const allScores = [...scoreHistory, { score: currentScore, timestamp: new Date().toISOString() }];
      
      // Filter out any invalid timestamps and sort chronologically (oldest to newest)
      const validScores = allScores.filter(item => {
        const date = new Date(item.timestamp);
        return !isNaN(date.getTime());
      });
      
      return validScores.sort((a, b) => {
        const dateA = new Date(a.timestamp);
        const dateB = new Date(b.timestamp);
        return dateA.getTime() - dateB.getTime();
      });
    };
    const scoreHistory = getAccountScoreHistory();
    
    // Validate that we have valid data for the chart
    
    const chartLabels = scoreHistory.map(item => {
      if (!item.timestamp) return 'N/A';
      const date = new Date(item.timestamp);
      if (isNaN(date.getTime())) return 'N/A';
      
      // Format date for better readability
      const now = new Date();
      const isToday = date.toDateString() === now.toDateString();
      const isYesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000).toDateString() === date.toDateString();
      
      if (isToday) {
        return `Today, ${date.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: true 
        })}`;
      } else if (isYesterday) {
        return `Yesterday, ${date.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: true 
        })}`;
      } else {
        return date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        });
      }
    });
    const chartScores = scoreHistory.map(item => item.score);
    
    // Calculate if score is trending upward over time (chronologically)
    const isScoreIncreasing = scoreHistory.length > 1 && 
      scoreHistory[scoreHistory.length - 1].score > scoreHistory[0].score;
    
    // Default to green if only one data point or if score is increasing
    const lineColor = scoreHistory.length === 1 || isScoreIncreasing ? 'rgba(16,185,129,1)' : 'rgba(239,68,68,1)';
    const fillColor = scoreHistory.length === 1 || isScoreIncreasing ? 'rgba(16,185,129,0.10)' : 'rgba(239,68,68,0.10)';
    // Ensure we have valid data for the chart
    
    const chartData = {
      labels: chartLabels || [],
      datasets: [
        {
          label: 'Account Score',
          data: chartScores || [],
          // We draw the fill per-segment via plugin; keep dataset itself unfilled
          backgroundColor: 'transparent',
          borderWidth: 3,
          fill: false,
          tension: 0.4,
          pointBackgroundColor: lineColor,
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 5,
          segment: {
            borderColor: ctx => {
              if (!ctx.p0 || !ctx.p1) return 'rgba(16,185,129,1)'; // default green
              return ctx.p1.parsed.y > ctx.p0.parsed.y
                ? 'rgba(16,185,129,1)' // green for rising
                : ctx.p1.parsed.y < ctx.p0.parsed.y
                ? 'rgba(239,68,68,1)'  // red for falling
                : '#64748b';           // gray for flat
            }
          }
        }
      ]
    };
    
    // Validate chart data before creating options
    
    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          displayColors: false,
          backgroundColor: '#ffffff',
          titleColor: '#111827',
          bodyColor: '#111827',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          padding: 12,
          callbacks: {
            title: (items) => items?.[0]?.label || '',
            label: (ctx) => `Score: ${ctx.parsed.y}`
          }
        },
        datalabels: { display: false }
      },
      animation: {
        duration: 1200,
        easing: 'easeOutQuart'
      },
      layout: {
        padding: {
          left: 8,
          right: 8,
          top: 8,
          bottom: 0
        }
      },
      elements: {
        line: {
          borderWidth: 3,
          borderColor: lineColor
        },
        point: {
          radius: 0,
          backgroundColor: lineColor,
          borderColor: '#fff',
          borderWidth: 3,
          hoverRadius: 6
        }
      },
      scales: {
        y: {
          min: 0,
          max: 100,
          grid: { color: '#eef2f7' },
          ticks: { stepSize: 20, color: '#94a3b8', font: { size: 12 } },
          title: { display: true, text: 'Score', color: '#64748b', font: { size: 14, weight: 'bold' } }
        },
        x: {
          grid: { display: false },
          ticks: {
            color: '#94a3b8',
            font: { size: 11 },
            maxRotation: 20,
            autoSkip: true,
            autoSkipPadding: 12,
            maxTicksLimit: 5,
            callback: (val, idx, ticks) => {
              const label = chartLabels?.[val] || '';
              // Ensure we show the full label for better timeline clarity
              return String(label);
            }
          },
          title: { display: true, text: 'Timeline', color: '#64748b', font: { size: 14, weight: 'bold' } }
        }
      }
    };

    // Risk badge logic
    const scoreValue = parseInt(companyInsight.account_score.split('/')[0]);
    let riskLabel = '';
    let riskStyle = {};
    if (scoreValue >= 67) {
      riskLabel = 'Low risk';
      riskStyle = {
        background: '#22c55e',
        color: '#fff',
        fontWeight: 600,
        fontSize: 8,
        borderRadius: 6,
        padding: '6px 12px',
        marginLeft: 2
      };
    } else if (scoreValue >= 33) {
      riskLabel = 'Medium risk';
      riskStyle = {
        background: '#f59e42',
        color: '#fff',
        fontWeight: 600,
        fontSize: 8,
        borderRadius: 6,
        padding: '6px 12px',
        marginLeft: 2
      };
    } else {
      riskLabel = 'High';
      riskStyle = {
        background: '#ef4444',
        color: '#fff',
        fontWeight: 600,
        fontSize: 8,
        borderRadius: 6,
        padding: '6px 12px',
        marginLeft: 2
      };
    }

    return (
      <>
        {/* Processing Toaster */}
        {showProcessingToaster && (
          <div className="bg-white text-gray-900 p-5 rounded-xl shadow-lg mb-6 border border-gray-200 relative border-l-4 border-l-green-500">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-lg">🔄</span>
              <span className="font-semibold text-sm text-gray-900">Processing New Insights</span>
            </div>
            <div className="text-sm leading-relaxed text-gray-600">
              Aura has identified {lastActivityCount} new engagements for this deal and is now generating insights in the background
            </div>
            <button
              onClick={() => setShowProcessingToaster(false)}
              className="absolute top-3 right-3 bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900 rounded-full w-6 h-6 flex items-center justify-center text-xs transition-all duration-200"
            >
              × 
            </button>
          </div>
        )}

        {/* Company Overview */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-l font-bold text-gray-900 m-0">
              {companyInsight.account_level_briefing} 
            </h2>
            <div className="flex items-center gap-3">
              {dealAmount && (
                <div className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg px-3 py-2 font-semibold text-sm">
                  ${parseInt(dealAmount).toLocaleString()}
                </div>
              )}
              {lastActivityCount !== null && lastActivityCount > 0 && (
                <div className="bg-yellow-50 text-yellow-800 rounded-lg px-3 py-2 font-semibold text-sm border border-yellow-200">
                  +{lastActivityCount} new activity
                </div>
              )}
              {lastActivityTimestamp && (
                <div className="bg-gray-50 text-gray-700 rounded-lg px-3 py-2 font-medium text-sm border border-gray-200">
                  Last activity: {formatTimeAgo(lastActivityTimestamp)}
                </div>
              )}
              <span className={`px-3 py-2 rounded-lg font-semibold text-sm ${
                scoreValue >= 67 ? 'bg-green-500 text-white' :
                scoreValue >= 33 ? 'bg-orange-500 text-white' :
                'bg-red-500 text-white'
              }`}>
                {riskLabel}
              </span>
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg px-3 py-2 font-semibold text-sm flex items-center gap-2">
                {companyInsight.account_score.split('/')[0]} %
                
                {(() => {
                  const scoreDiff = calculateScoreDifference(companyInsight.account_score, insightsLog);
                  if (scoreDiff) {
                    const arrowDirection = scoreDiff.difference > 0 ? '↗' : scoreDiff.difference < 0 ? '↘' : '→';
                    const arrowColor = scoreDiff.difference > 0 
                      ? 'bg-green-100 text-green-800' 
                      : scoreDiff.difference < 0 
                      ? 'bg-red-100 text-red-800' 
                      : 'bg-gray-100 text-gray-800';
                    
                    return (
                      <span
                        className={`flex items-center gap-1 text-xs px-2 py-1 rounded border border-gray-200 font-semibold cursor-pointer ${arrowColor}`}
                        title='View score trend'
                        onClick={() => setShowScoreTrend(true)}
                      >
                        <span className="text-xs">
                          {arrowDirection}
                        </span>
                        <span>
                          {scoreDiff.difference > 0 ? '+' : ''}{scoreDiff.difference}
                        </span>
                      </span>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>
          </div>
          <div className="text-sm text-gray-600 leading-relaxed p-4 bg-gray-50 rounded-lg border border-gray-200">
            {companyInsight.brief_summary}
          </div>
        </div>

        {/* Signal Types Badges (only for signals mapped to NBA) */}
        <div className="mb-2 mt-4">
          <div className="font-semibold text-sm text-gray-900 mb-3 flex items-center gap-2 tracking-wide">
            <FiRadio className="w-4 h-4" />
            Signals Driving Your Next Best Actions
          </div>
          <div className="flex flex-wrap gap-3 mb-6">
            {mappedSignals.map((signal, idx) => {
              const signalType = signal?.signal_type || '';
              const [category, signalTypeBadge] = signalType.split('::');
              const color = signalTypeColors[category] || signalTypeColors.Default;
              return signalTypeBadge && (
                <span key={signal.signal_id || idx} className="px-3 py-2 rounded-lg text-sm font-semibold tracking-wide inline-block border border-gray-200"
                     style={{ background: color.bg, color: color.color }}>
                  {signalTypeBadge.replace(/_/g, ' ')}
                </span>
              );
            })}
          </div>
        </div>

      


  {/* GTM Playbook Section */}
  <Section title="GTM Taskbook" iconComponent={FiBookOpen} collapsible expanded={gtmExpanded} onToggle={onToggleGtm}>
          {gtmExpanded && (
            <div>
              <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
                {companyInsight.aura_insight_detected?.gtm_paths_you_can_pursue?.map((path, idx) => (
                  <div key={idx} className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 transition-all duration-200 hover:shadow-2xl hover:-translate-y-1">
                    <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100">
                      <h3 className="text-lg font-semibold text-gray-900 m-0">
                        {path.title}
                      </h3>
                      <div className="bg-green-50 text-green-800 rounded-lg px-3 py-2 font-semibold text-sm">
                        Impact: {path.score_impact}
                      </div>
                    </div>
                    {/* Path Steps */}
                    <div className="mb-6">
                      <div className="text-sm text-gray-500 font-semibold mb-3">IMPLEMENTATION TASKS</div>
                      <ul className="space-y-3">
                        {path.path_steps.map((step, sidx) => (
                          <li key={sidx} className="p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm text-gray-700 leading-relaxed flex items-center justify-between">
                            <div className="flex items-center flex-1">
                              <input
                                type="checkbox"
                                checked={(gtmSelectedSteps[idx] || []).includes(sidx)}
                                onChange={() => toggleGtmStepSelected(idx, sidx)}
                                className="mr-3 scale-125 cursor-pointer"
                              />
                              <span className="flex-1">{step}</span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    {/* Why This Works */}
                    <details className="mt-6">
                      <summary className="cursor-pointer text-blue-600 font-semibold text-sm py-2 border-t border-gray-100 pt-4">
                        🔍 Why this works
                      </summary>
                      <div className="text-sm text-gray-600 leading-relaxed mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        {path.why_this_works}
                      </div>
                    </details>
                    
                    {/* Add Task Button (bulk via selected steps) */}
                    <button
                      onClick={() => handleAddGtmTasks(path, idx)}
                      disabled={gtmAddingIndex === idx || !!gtmAddedByPath[idx]}
                      className={`w-full mt-6 px-4 py-3 rounded-lg font-semibold text-sm transition-all duration-200 ${
                        gtmAddedByPath[idx] 
                          ? 'bg-green-500 text-white cursor-not-allowed' 
                          : gtmAddingIndex === idx 
                          ? 'bg-gray-400 text-white cursor-not-allowed opacity-80' 
                          : 'bg-gray-800 text-white hover:bg-gray-900 cursor-pointer'
                      }`}
                    >
                      {gtmAddedByPath[idx] ? '✅ Added' : (gtmAddingIndex === idx ? 'Adding tasks...' : '📋 Add selected as tasks')}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Section>


 {/* Next Best Actions Section */}
          <Section title="Next Best Actions" iconComponent={FiZap} collapsible expanded={nbaExpanded} onToggle={onToggleNba}>
          {nbaExpanded && (
            <div className="space-y-4">
              {nbaActions.map((nba, idx) => (
                <div key={nba.signal_reference_id || idx} className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 transition-all duration-200 hover:shadow-2xl hover:-translate-y-1">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${idx === 0 ? 'bg-green-500' : 'bg-blue-500'}`}></div>
                      <h3 className="text-lg font-bold text-gray-900 leading-tight max-w-[70%] m-0">
                        {nba.action_title}
                      </h3>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        className={`px-4 py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 min-w-[120px] ${
                          idx === 0
                            ? 'bg-green-500 hover:bg-green-600 text-white'
                            : 'bg-blue-500 hover:bg-blue-600 text-white'
                        } ${(loadingActionIndex === idx || (isLoadingTemplates && pendingActionIndex === idx)) ? 'opacity-80 cursor-not-allowed' : 'cursor-pointer'}`}
                        onClick={e => { e.stopPropagation(); handleExecute(idx); }}
                        disabled={loadingActionIndex === idx || (isLoadingTemplates && pendingActionIndex === idx)}
                      >
                        {isLoadingTemplates && pendingActionIndex === idx
                          ? 'Loading...'
                          : loadingActionIndex === idx
                          ? 'Drafting...'
                          : 'Draft an email'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Custom Action Card - Only show when there are exactly 2 NBA actions */}
              {nbaActions.length === 2 && (
                <div className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 transition-all duration-200 hover:shadow-2xl hover:-translate-y-1">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-indigo-500"></div>
                      <h3 className="text-lg font-bold text-gray-900 leading-tight max-w-[70%] m-0">
                        Draft Email Using Both NBA Actions
                      </h3>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        className={`px-5 py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 min-w-[120px] bg-indigo-500 hover:bg-indigo-600 text-white ${
                          (loadingActionIndex === 3 || (isLoadingTemplates && pendingActionIndex === 3)) ? 'opacity-80 cursor-not-allowed' : 'cursor-pointer'
                        }`}
                        onClick={() => handleExecute(3)}
                        disabled={loadingActionIndex === 3 || (isLoadingTemplates && pendingActionIndex === 3)}
                      >
                        {isLoadingTemplates && pendingActionIndex === 3
                          ? 'Loading...'
                          : loadingActionIndex === 3
                          ? 'Drafting...'
                          : 'Draft an email'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </Section>


        {/* Key Risks & Opportunities Section */}
        <Section title="Key Risks & Opportunities" iconComponent={FiAlertTriangle} collapsible expanded={risksExpanded} onToggle={onToggleRisks}>
          {risksExpanded && (
            <div className="space-y-4">
              {/* Warning Signals */}
              {companyInsight.early_warning_signal?.map((signal, idx) => (
                <div key={idx} className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 transition-all duration-200">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-red-600 text-sm">⚠️</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-red-700 mb-2">Warning Signal</h4>
                      <div className="text-sm text-gray-700 leading-relaxed">
                        {signal.warning_signal}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {/* Aura Insight */}
              {companyInsight.aura_insight_detected && (
                <div className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 transition-all duration-200">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-yellow-600 text-sm">💡</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-yellow-800 mb-2">
                        {companyInsight.aura_insight_detected.insight_label}
                      </h4>
                      <div className="text-sm text-gray-700 leading-relaxed">
                        {companyInsight.aura_insight_detected.insight_explanation}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </Section>

      

        {/* Coaching Tip Section */}
        <Section title="Coaching Tip" iconComponent={FiTarget} collapsible expanded={coachingExpanded} onToggle={onToggleCoaching}>
          {coachingExpanded && (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 transition-all duration-200">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-blue-600 text-sm">🎯</span>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-blue-700 mb-2">Coaching Tip</h4>
                  <div className="text-sm text-gray-700 leading-relaxed">
                    {companyInsight.coaching_tip}
                  </div>
                </div>
              </div>
            </div>
          )}
        </Section>

        {/* Add Note Button - Only show for deals */}
        {objectId && (
          <div className="mt-8 flex justify-end pr-6">
            <button
              onClick={() => setShowNoteDialog(true)}
              className="w-14 h-14 bg-gray-800 hover:bg-gray-900 text-white border-none rounded-full cursor-pointer text-2xl font-bold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-110 flex items-center justify-center"
              title="Add Note to Deal"
            >
              +
            </button>
          </div>
        )}
      </>
    );
  };

  // Stable toggle callbacks to avoid new function instances each render
  const onToggleRisks = React.useCallback(() => setRisksExpanded(v => !v), []);
  const onToggleGtm = React.useCallback(() => setGtmExpanded(v => !v), []);
  const onToggleCoaching = React.useCallback(() => setCoachingExpanded(v => !v), []);
  const onToggleNba = React.useCallback(() => setNbaExpanded(v => !v), []);

  // Note: Avoid memoizing the entire insights view to prevent subtree remounts and flashes on toggle

  // Main render
  return (
    <div className="bg-gray-50 font-sans text-gray-900 antialiased min-h-screen">
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
          
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">
              Deal Insights
              <sub className="text-sm font-semibold text-gray-500 ml-2 tracking-wide">
                by AURA
              </sub>
            </h1>
          </div>
        </div>
        
        <hr className="border-gray-200 mb-8" />
      
      {/* Notification Display */}
      {notification && (
        <div className={`p-3 rounded-lg mb-4 border transition-all duration-300 ${
          notification.type === 'success' 
            ? 'bg-green-50 border-green-200 text-green-800' 
            : notification.type === 'error' 
            ? 'bg-red-50 border-red-200 text-red-800' 
            : 'bg-blue-50 border-blue-200 text-blue-800'
        }`}>
          <div className="flex items-center justify-between">
            <span className="font-semibold text-sm">
              {notification.type === 'success' ? '✅ Success' : 
               notification.type === 'error' ? '❌ Error' : 'ℹ️ Info'}
            </span>
            <button
              onClick={() => setNotification(null)}
              className="text-inherit opacity-70 hover:opacity-100 transition-opacity"
            >
              ×
            </button>
          </div>
          <div className="mt-1 text-sm">
            {notification.message}
          </div>
        </div>
      )}
      
      {loading && <Loading />}
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 mb-4 text-sm">
          {error}
        </div>
      )}
      
      {insightData && (
        <div>
          {renderDealInsights()}
        </div>
      )}

      {/* Document Generator — always visible when deal context is available */}
      {objectId && !loading && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 mb-6 mt-2">
          <div className="flex items-center justify-between">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
                <FiBookOpen className="text-indigo-500 w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 m-0">Generate a Document</h3>
                <p className="text-sm text-gray-500 mt-1 m-0">
                  {insightData
                    ? 'Pick a template to create a branded document for this deal — no NBA context required.'
                    : 'No engagement data yet. You can still generate a templated document for this deal.'}
                </p>
              </div>
            </div>
            <button
              onClick={handleGenerateDocument}
              disabled={isGeneratingDoc || (isLoadingTemplates && isTemplateOnlyMode)}
              className={`ml-6 px-5 py-2.5 rounded-lg font-semibold text-sm text-white border-none transition-all duration-200 flex-shrink-0 ${
                isGeneratingDoc || (isLoadingTemplates && isTemplateOnlyMode)
                  ? 'bg-indigo-300 cursor-not-allowed'
                  : 'bg-indigo-600 hover:bg-indigo-700 cursor-pointer'
              }`}
            >
              {isGeneratingDoc
                ? 'Generating...'
                : isLoadingTemplates && isTemplateOnlyMode
                ? 'Loading templates...'
                : 'Generate Document'}
            </button>
          </div>
        </div>
      )}

      <style>{``}</style>

      {/* Template Selection Modal */}
      {showTemplateModal && (
        <Modal open={showTemplateModal} onClose={() => {
          setShowTemplateModal(false);
          setPendingActionIndex(null);
          setTemplateSearch('');
          setIsTemplateOnlyMode(false);
        }}>
          <div style={{ width: '680px', maxWidth: '95vw', maxHeight: '85vh' }} className="bg-white rounded-xl overflow-hidden shadow-2xl flex flex-col">
            {/* Header */}
            <div className="p-6 pb-4 border-b border-gray-200 bg-gray-50 flex justify-between items-start flex-shrink-0">
              <div>
                <h3 className="text-lg font-bold text-gray-900 m-0">
                  {isTemplateOnlyMode ? 'Generate a Document' : 'Select a Document Template'}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {isTemplateOnlyMode
                    ? `${availableTemplates.length} templates — pick one to generate a branded document for this deal`
                    : `${availableTemplates.length} templates available — choose one to generate a tailored document`}
                </p>
              </div>
              <button
                onClick={() => { setShowTemplateModal(false); setPendingActionIndex(null); setTemplateSearch(''); setIsTemplateOnlyMode(false); }}
                className="bg-gray-100 hover:bg-gray-200 border-none rounded-full w-8 h-8 flex items-center justify-center cursor-pointer text-gray-600 text-lg transition-colors flex-shrink-0 ml-4"
              >
                ×
              </button>
            </div>

            {/* Search */}
            <div className="px-5 pt-4 pb-3 flex-shrink-0">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search templates..."
                  value={templateSearch}
                  onChange={e => setTemplateSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                  autoFocus
                />
                {templateSearch && (
                  <button
                    onClick={() => setTemplateSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 border-none bg-transparent cursor-pointer p-0 leading-none"
                  >
                    ×
                  </button>
                )}
              </div>
              {templateSearch && (
                <p className="text-xs text-gray-400 mt-1.5">
                  {availableTemplates.filter(t =>
                    t.key.toLowerCase().includes(templateSearch.toLowerCase()) ||
                    (t.description || '').toLowerCase().includes(templateSearch.toLowerCase())
                  ).length} results
                </p>
              )}
            </div>

            {/* Template list */}
            <div className="overflow-y-auto flex-1 px-5 pb-2">
              {(() => {
                const filtered = availableTemplates.filter(t =>
                  t.key.toLowerCase().includes(templateSearch.toLowerCase()) ||
                  (t.description || '').toLowerCase().includes(templateSearch.toLowerCase())
                );
                if (filtered.length === 0) {
                  return (
                    <div className="text-center py-10 text-gray-400 text-sm">
                      No templates match &ldquo;{templateSearch}&rdquo;
                    </div>
                  );
                }
                return (
                  <div className="space-y-1.5">
                    {filtered.map(t => (
                      <button
                        key={t.id}
                        onClick={() => {
                          setShowTemplateModal(false);
                          setTemplateSearch('');
                          if (isTemplateOnlyMode) {
                            setIsTemplateOnlyMode(false);
                            handleTemplateOnlyGenerate(t.id);
                          } else {
                            const idx = pendingActionIndex;
                            setPendingActionIndex(null);
                            executeNbaWithTemplate(idx, t.id);
                          }
                        }}
                        className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all duration-150 cursor-pointer group"
                      >
                        <div className="text-sm font-semibold text-gray-900 group-hover:text-blue-700">{t.key}</div>
                        {t.description && (
                          <div className="text-xs text-gray-500 mt-0.5 leading-relaxed line-clamp-2">{t.description}</div>
                        )}
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>

            {/* Footer */}
            <div className="px-5 py-4 border-t border-gray-100 flex justify-between items-center flex-shrink-0 bg-gray-50">
              <span className="text-xs text-gray-400">Powered by Ariya</span>
              {!isTemplateOnlyMode && (
                <button
                  onClick={() => {
                    const idx = pendingActionIndex;
                    setShowTemplateModal(false);
                    setPendingActionIndex(null);
                    setTemplateSearch('');
                    executeNbaWithTemplate(idx, null);
                  }}
                  className="px-4 py-2 bg-gray-800 hover:bg-gray-900 text-white border-none rounded-lg font-semibold text-sm cursor-pointer transition-all duration-200"
                >
                  Skip — email only
                </button>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <Modal open={showEmailModal} onClose={() => {
          setShowEmailModal(false);
          setEmailHasChanges(false);
          setEmailEditError(null);
          setCollateralType('');
        }}>
          <div className="w-[90vw] max-w-4xl h-[90vh] flex flex-col bg-white rounded-xl overflow-hidden">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-bold text-gray-900 m-0">
                  Email Draft
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Review and edit the generated email before sending
                </p>
                {nbaIndex !== null && (
                  <div className="mt-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Action #{nbaIndex + 1}
                    </span>
                  </div>
                )}
              </div>
              <button
                onClick={() => {
                  setShowEmailModal(false);
                  setEmailHasChanges(false);
                  setEmailEditError(null);
                  setCollateralType('');
                }}
                className="bg-gray-100 hover:bg-gray-200 border-none rounded-full w-8 h-8 flex items-center justify-center cursor-pointer text-gray-600 text-lg transition-colors"
              >
                ×
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 p-6 overflow-auto">
              {emailEditError && (
                <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-lg mb-6 text-sm">
                  {emailEditError}
                </div>
              )}

              <div
                ref={emailEditorRef}
                contentEditable={true}
                suppressContentEditableWarning={true}
                onInput={(e) => {
                  const currentContent = e.target.innerHTML;
                  const hasChanges = currentContent !== originalEmailHtmlRef.current;
                  setEmailHasChanges(hasChanges);
                  console.log('[DealInsights] Email content changed. Has changes:', hasChanges);
                }}
                className="border border-gray-300 rounded-lg p-4 min-h-[300px] text-base leading-relaxed outline-none bg-white text-gray-900"
              />

              {attachmentUrl && (
                <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg flex items-center gap-3">
                  <FiPaperclip className="text-gray-500" />
                  <span className="text-sm text-gray-700">Attachment:</span>
                  <a
                    href={attachmentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 no-underline"
                  >
                    View PDF
                  </a>
                </div>
              )}

              {collateralAsset && (
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3">
                  <FiBookOpen className="text-blue-500" />
                  <span className="text-sm text-blue-700">Asset ready:</span>
                  <button
                    onClick={async () => {
                      try {
                        if (collateralAsset?.ariya_document_id) {
                          const res = await apiService.getAriyaHandoff(
                            collateralAsset.ariya_document_id,
                            collateralAsset.contact_email
                          );
                          window.open(res.data.redirectUrl, '_blank');
                        } else {
                          const baseUrl = window.location.origin;
                          const redirectTarget = `${baseUrl}/tailspin/collateral/${collateralSlug}?source=aura`;
                          window.open(`${baseUrl}/callback?token=${localStorage.getItem('aura_token')}&tenant_id=${localStorage.getItem('aura_tenant_id')}&redirect_url=${encodeURIComponent(redirectTarget)}`, '_blank');
                        }
                      } catch {
                        setNotification({ type: 'error', message: 'Could not open document. Please try again.' });
                      }
                    }}
                    className="text-blue-600 hover:text-blue-800 font-medium bg-transparent border-none cursor-pointer p-0 underline"
                  >
                    View Asset
                  </button>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
              <div className={`text-sm ${emailHasChanges ? 'text-orange-600' : 'text-gray-500'}`}>
                {emailHasChanges ? '⚠️ Unsaved changes' : '✅ No changes'}
              </div>
              <div className="flex gap-3">
                <button
                  className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 ${
                    emailHasChanges 
                      ? 'bg-orange-500 hover:bg-orange-600 text-white cursor-pointer' 
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                  disabled={!emailHasChanges}
                  onClick={() => {
                    if (!emailHasChanges) return;
                    
                    // Debug logging
                    console.log('🔍 Save button clicked - entityId:', entityId, 'nbaIndex:', nbaIndex);
                    
                    if (!entityId) {
                      setNotification({ type: 'error', message: 'Entity ID is missing. Please refresh and try again.' });
                      return;
                    }
                    
                    const latestHtml = emailEditorRef.current ? emailEditorRef.current.innerHTML : draftedEmailHtml;
                    saveCollateral(entityId, latestHtml, attachmentUrl, nbaIndex)
                      .then((result) => {
                        if (result && result.success) {
                          setNotification({ type: 'success', message: 'Email changes saved successfully!' });
                        } else {
                          setNotification({ type: 'error', message: 'Failed to save email changes.' });
                        }
                      })
                      .catch((error) => {
                        console.error('Error saving collateral:', error);
                        setNotification({ type: 'error', message: 'Error saving email changes.' });
                      });
                    setDraftedEmailHtml(latestHtml);
                    setOriginalEmailHtml(latestHtml);
                    setEmailHasChanges(false);
                    setShowEmailModal(false);
                  }}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      

      {/* Score Trend Modal */}
      {showScoreTrend && insightData && (
        <Modal open={showScoreTrend && insightData && insightData.insight && insightData.insight.companyinsight} onClose={() => setShowScoreTrend(false)}>
          <div className="w-full max-w-2xl mx-auto rounded-2xl shadow-xl p-6 bg-white relative">
            <h4 className="text-2xl font-bold text-gray-900 tracking-wide text-center m-0 mb-6">
              📊 Account Score Trend
            </h4>
            <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
              {(() => {
                // We need to regenerate the chart data here since it's scoped inside renderDealInsights
                const companyInsight = insightData?.insight?.companyinsight;
                if (!companyInsight) return null;

                const getModalScoreHistory = () => {
                  if (!insightData || !companyInsight?.account_score) return [];
                  
                  if (!insightsLog || insightsLog.length === 0) {
                    const currentScore = parseInt(companyInsight.account_score.split('/')[0]);
                    return [{ score: currentScore, timestamp: new Date().toISOString() }];
                  }
                  
                  const scoreHistory = insightsLog
                    .map(log => {
                      const scoreStr = log?.insight?.companyinsight?.account_score || 
                                      log?.companyinsight?.account_score ||
                                      log?.account_score;
                      
                      if (!scoreStr) return null;
                      
                      const num = parseInt(scoreStr.split('/')[0]);
                      if (isNaN(num)) return null;
                      
                      const timestamp = log.created_at || log.timestamp || log.insight?.created_at;
                      
                      return { score: num, timestamp: timestamp };
                    })
                    .filter(v => v !== null);
                  
                  const currentScore = parseInt(companyInsight.account_score.split('/')[0]);
                  const allScores = [...scoreHistory, { score: currentScore, timestamp: new Date().toISOString() }];
                  
                  const validScores = allScores.filter(item => {
                    const date = new Date(item.timestamp);
                    return !isNaN(date.getTime());
                  });
                  
                  return validScores.sort((a, b) => {
                    const dateA = new Date(a.timestamp);
                    const dateB = new Date(b.timestamp);
                    return dateA.getTime() - dateB.getTime();
                  });
                };

                const modalScoreHistory = getModalScoreHistory();
                
                if (modalScoreHistory.length === 0) {
                  return (
                    <div className="h-56 flex items-center justify-center text-gray-500 text-sm text-center p-5">
                      <div>
                        <div className="text-base mb-2">📊</div>
                        <div>No score history data available</div>
                        <div className="text-xs mt-2 text-gray-400">
                          The chart will appear once score data is available
                        </div>
                      </div>
                    </div>
                  );
                }

                const modalChartLabels = modalScoreHistory.map(item => {
                  if (!item.timestamp) return 'N/A';
                  const date = new Date(item.timestamp);
                  if (isNaN(date.getTime())) return 'N/A';
                  
                  const now = new Date();
                  const isToday = date.toDateString() === now.toDateString();
                  const isYesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000).toDateString() === date.toDateString();
                  
                  if (isToday) {
                    return `Today, ${date.toLocaleTimeString('en-US', { 
                      hour: '2-digit', 
                      minute: '2-digit',
                      hour12: true 
                    })}`;
                  } else if (isYesterday) {
                    return `Yesterday, ${date.toLocaleTimeString('en-US', { 
                      hour: '2-digit', 
                      minute: '2-digit',
                      hour12: true 
                    })}`;
                  } else {
                    return date.toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    });
                  }
                });

                const modalChartScores = modalScoreHistory.map(item => item.score);
                const modalIsScoreIncreasing = modalScoreHistory.length > 1 && 
                  modalScoreHistory[modalScoreHistory.length - 1].score > modalScoreHistory[0].score;
                const modalLineColor = modalScoreHistory.length === 1 || modalIsScoreIncreasing ? 'rgba(16,185,129,1)' : 'rgba(239,68,68,1)';

                const modalChartData = {
                  labels: modalChartLabels || [],
                  datasets: [
                    {
                      label: 'Account Score',
                      data: modalChartScores || [],
                      backgroundColor: 'transparent',
                      borderWidth: 3,
                      fill: false,
                      tension: 0.4,
                      pointBackgroundColor: modalLineColor,
                      pointBorderColor: '#fff',
                      pointBorderWidth: 2,
                      pointRadius: 0,
                      pointHoverRadius: 5,
                      segment: {
                        borderColor: ctx => {
                          if (!ctx.p0 || !ctx.p1) return 'rgba(16,185,129,1)';
                          return ctx.p1.parsed.y > ctx.p0.parsed.y
                            ? 'rgba(16,185,129,1)'
                            : ctx.p1.parsed.y < ctx.p0.parsed.y
                            ? 'rgba(239,68,68,1)'
                            : '#64748b';
                        }
                      }
                    }
                  ]
                };

                const modalChartOptions = {
                  responsive: true,
                  maintainAspectRatio: false,
                  interaction: { mode: 'index', intersect: false },
                  plugins: {
                    legend: { display: false },
                    tooltip: {
                      enabled: true,
                      displayColors: false,
                      backgroundColor: '#ffffff',
                      titleColor: '#111827',
                      bodyColor: '#111827',
                      borderColor: '#e5e7eb',
                      borderWidth: 1,
                      padding: 12,
                      callbacks: {
                        title: (items) => items?.[0]?.label || '',
                        label: (ctx) => `Score: ${ctx.parsed.y}`
                      }
                    },
                    datalabels: { display: false }
                  },
                  animation: {
                    duration: 1200,
                    easing: 'easeOutQuart'
                  },
                  layout: {
                    padding: { left: 8, right: 8, top: 8, bottom: 0 }
                  },
                  elements: {
                    line: { borderWidth: 3, borderColor: modalLineColor },
                    point: {
                      radius: 0,
                      backgroundColor: modalLineColor,
                      borderColor: '#fff',
                      borderWidth: 3,
                      hoverRadius: 6
                    }
                  },
                  scales: {
                    y: {
                      min: 0,
                      max: 100,
                      grid: { color: '#eef2f7' },
                      ticks: { stepSize: 20, color: '#94a3b8', font: { size: 12 } },
                      title: { display: true, text: 'Score', color: '#64748b', font: { size: 14, weight: 'bold' } }
                    },
                    x: {
                      grid: { display: false },
                      ticks: {
                        color: '#94a3b8',
                        font: { size: 11 },
                        maxRotation: 20,
                        autoSkip: true,
                        autoSkipPadding: 12,
                        maxTicksLimit: 5
                      },
                      title: { display: true, text: 'Timeline', color: '#64748b', font: { size: 14, weight: 'bold' } }
                    }
                  }
                };

                return (
                  <Line
                    data={modalChartData}
                    options={modalChartOptions}
                    height={220}
                  />
                );
              })()}
            </div>
          </div>
        </Modal>
      )}

      {/* Note Dialog Modal */}
      <Modal open={showNoteDialog} contentStyle={{ padding: '8px' }} onClose={() => {
        setShowNoteDialog(false);
        setNoteText('');
        setNoteSuccess(false);
      }}>
        <div className="w-96 bg-white rounded-xl overflow-hidden shadow-2xl">
          {/* Header */}
          <div className="p-6 pb-0 bg-white">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900 m-0 leading-tight">
                  Add Note to Deal
                </h3>
                <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                  Your note will be saved directly to this deal in HubSpot
                </p>
              </div>
              <button
                onClick={() => {
                  setShowNoteDialog(false);
                  setNoteText('');
                  setNoteSuccess(false);
                }}
                className="bg-gray-100 hover:bg-gray-200 border-none rounded-full w-8 h-8 flex items-center justify-center cursor-pointer text-gray-600 text-lg transition-all duration-200"
              >
                ×
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 pb-6">
            {noteSuccess ? (
              <div className="text-center py-10 px-5">
                <div className="text-5xl mb-4">
                  ✅
                </div>
                <h4 className="text-lg font-semibold text-green-600 m-0 mb-2">
                  Success!
                </h4>
                <p className="text-sm text-gray-600 leading-relaxed m-0">
                  Your note has been saved to the deal in HubSpot
                </p>
              </div>
            ) : (
              <>
                <textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Enter your thoughts, observations, or next steps for this deal..."
                  className="w-full h-32 p-4 border-2 border-gray-200 rounded-lg text-sm leading-relaxed resize-vertical outline-none font-inherit transition-colors focus:border-blue-500"
                />

                <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-100">
                  <button
                    onClick={() => {
                      setShowNoteDialog(false);
                      setNoteText('');
                      setNoteSuccess(false);
                    }}
                    className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 border-none rounded-lg font-semibold text-sm cursor-pointer transition-all duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddNote}
                    disabled={isAddingNote || !noteText.trim()}
                    className={`px-6 py-2.5 border-none rounded-lg font-bold text-sm transition-all duration-200 flex items-center gap-2 ${
                      isAddingNote || !noteText.trim() 
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                        : 'bg-gray-800 hover:bg-gray-900 text-white cursor-pointer'
                    }`}
                  >
                    {isAddingNote ? (
                      <>
                        <span>Adding...</span>
                      </>
                    ) : (
                      <>
                        <FiSend size={14} />
                        Add Note to Deal
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </Modal>
    </div>
  </div>
  );
};

export default DealInsights;
